import { FC, ReactNode, createContext, useCallback, useState } from "react";

import { useSelector } from "@/store";

type DialogType =
  | "terms"
  | "forgot-password"
  | "reset-password"
  | "auth"
  | "vip"
  | "2fa"
  | "wallet"
  | "wallet-settings"
  | "daily-spin"
  | null;

export interface DialogContextProps {
  openDialog: (dialog: DialogType, data?: unknown) => void;
  closeDialog: () => void;
  activeDialog: DialogType;
  data: unknown;
}

export const DialogContext = createContext<DialogContextProps | undefined>(undefined);

export const DialogProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<unknown>(null);
  const [activeDialog, setActiveDialog] = useState<DialogType>(null);
  const isLoggedIn = useSelector((store) => store.auth.isLoggedIn);

  const openDialog = useCallback(
    (dialog: DialogType, data?: unknown) => {
      if (dialog === "auth" && isLoggedIn) return;
      if (dialog === "wallet" && !isLoggedIn) {
        setActiveDialog("auth");
        return;
      }

      setActiveDialog(dialog);
      // Always replaced: a dialog opened without a payload must not inherit
      // the last one's, or "forgot password → back" lands on the register tab
      // because that was the tab the header had asked for earlier.
      setData(data ?? null);
    },
    [isLoggedIn],
  );

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setData(null);
  }, []);

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog, activeDialog, data }}>
      {children}
    </DialogContext.Provider>
  );
};
